'use client'

import { useEffect } from 'react'

type UsePaginationRelLinksParams = {
	prevHref?: string
	nextHref?: string
	isFirstPage: boolean
	isLastPage: boolean
}

const createRelLink = (rel: 'prev' | 'next', href: string): HTMLLinkElement => {
	const link = document.createElement('link')
	link.rel = rel
	link.href = href
	link.setAttribute('data-pagination-rel', rel)
	return link
}

/**
 * App Router friendly replacement for next/head pagination links.
 * Keeps rel=prev/next links in <head> based on current pagination state.
 */
export const usePaginationRelLinks = ({
	prevHref,
	nextHref,
	isFirstPage,
	isLastPage
}: UsePaginationRelLinksParams): void => {
	useEffect(() => {
		if (typeof document === 'undefined') return

		const createdLinks: HTMLLinkElement[] = []

		if (!isFirstPage && prevHref) {
			const prev = createRelLink('prev', prevHref)
			document.head.appendChild(prev)
			createdLinks.push(prev)
		}

		if (!isLastPage && nextHref) {
			const next = createRelLink('next', nextHref)
			document.head.appendChild(next)
			createdLinks.push(next)
		}

		return () => {
			createdLinks.forEach(link => {
				link.parentNode?.removeChild(link)
			})
		}
	}, [isFirstPage, isLastPage, nextHref, prevHref])
}
